import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loading } from '@evertrue/et-components';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import cadenceGqlCall from '../../utils/cadence-gql-call';
import { CADENCE_PERFORMANCE_QUERY } from '../../utils/cadence-queries';

const GROUPING_MAP = { days: 'DAY', weeks: 'WEEK', months: 'MONTH' };

const TIMEFRAME_TO_RANGE = {
  '1_week': 'DAYS_7',
  '1_month': 'DAYS_30',
  '3_months': 'DAYS_90',
  '6_months': 'DAYS_180',
  '12_months': 'DAYS_365',
  all_time: 'ALL_TIME',
};

const BULK_TIME_OPTIONS = [
  { label: '7D', value: '7d', range: 'DAYS_7', grouping: 'DAY' },
  { label: '30D', value: '30d', range: 'DAYS_30', grouping: 'DAY' },
  { label: '90D', value: '90d', range: 'DAYS_90', grouping: 'WEEK' },
];

/** Series definitions for email steps — colors match PM mockup */
const EMAIL_SERIES = [
  { key: 'emailsSent', label: 'Sent', color: '#8B5CF6' },
  { key: 'emailsDelivered', label: 'Delivered', color: '#3B82F6' },
  { key: 'emailsOpened', label: 'Opened', color: '#14B8A6' },
  { key: 'emailsClicked', label: 'Clicked', color: '#16A34A' },
  { key: 'emailsReplied', label: 'Replied', color: '#F59E0B' },
  { key: 'successReached', label: 'Success Reached', color: '#D97706' },
  { key: 'tasksSkipped', label: 'Skipped', color: '#94A3B8' },
  { key: 'emailsBounced', label: 'Error', color: '#EF4444' },
  { key: 'emailsUnsubscribed', label: 'Unsubscribed', color: '#9CA3AF' },
  { key: 'membersRemoved', label: 'Exited', color: '#F43F5E' },
];

/** Series definitions for manual task steps */
const TASK_SERIES = [
  { key: 'tasksCompleted', label: 'Tasks Completed', color: '#8B5CF6' },
  { key: 'tasksSkipped', label: 'Skipped', color: '#94A3B8' },
  { key: 'successReached', label: 'Success Reached', color: '#F59E0B' },
  { key: 'membersRemoved', label: 'Exited', color: '#F43F5E' },
];

/** Series for bulk message chart */
const BULK_SERIES = [
  { key: 'emailsSent', label: 'Sent', color: '#8B5CF6' },
  { key: 'emailsDelivered', label: 'Delivered', color: '#3B82F6' },
  { key: 'emailsOpened', label: 'Opened', color: '#14B8A6' },
  { key: 'emailsClicked', label: 'Clicked', color: '#16A34A' },
  { key: 'emailsReplied', label: 'Replied', color: '#F59E0B' },
  { key: 'successReached', label: 'Success Reached', color: '#D97706' },
  { key: 'emailsUnsubscribed', label: 'Unsubscribed', color: '#EF4444' },
];

/** Custom tooltip matching the app's style */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#374151' }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.name} style={{ color: entry.color, padding: '1px 0' }}>
          {entry.name}: {entry.value}
        </div>
      ))}
    </div>
  );
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

const CadencePerformanceChart = ({
  cadenceId,
  cadenceEventId,
  timeframe = '1_week',
  variant,
  title: titleProp,
  isManualStep = false,
}) => {
  const isBulk = variant === 'bulk';
  const [groupBy, setGroupBy] = useState(isBulk ? null : 'days');
  const [bulkTimeOption, setBulkTimeOption] = useState('7d');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hiddenSeries, setHiddenSeries] = useState(new Set());

  const activeBulkOption = BULK_TIME_OPTIONS.find((o) => o.value === bulkTimeOption) || BULK_TIME_OPTIONS[2];
  const timeRange = isBulk ? activeBulkOption.range : TIMEFRAME_TO_RANGE[timeframe] || 'DAYS_90';
  const grouping = isBulk ? activeBulkOption.grouping : GROUPING_MAP[groupBy];

  const seriesConfig = isBulk ? BULK_SERIES : isManualStep ? TASK_SERIES : EMAIL_SERIES;
  const visibleSeries = seriesConfig.filter((s) => !hiddenSeries.has(s.label));

  useEffect(() => {
    if (!cadenceId) return;
    let cancelled = false;
    setLoading(true);

    const variables = {
      cadenceId: String(cadenceId),
      timeRange,
      grouping,
    };
    if (cadenceEventId) variables.cadenceEventId = String(cadenceEventId);

    cadenceGqlCall(CADENCE_PERFORMANCE_QUERY, variables)
      .then((result) => {
        if (!cancelled) setData(result.cadencePerformance || []);
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cadenceId, cadenceEventId, grouping, timeRange]);

  const title = titleProp || (isBulk ? 'Performance Over Time' : 'Step Performance');

  const toggleSeries = (label) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const renderControls = () => {
    if (isBulk) {
      return (
        <div className="cadence-performance-chart--time-pills">
          {BULK_TIME_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`cadence-performance-chart--time-pill ${bulkTimeOption === opt.value ? 'is-active' : ''}`}
              onClick={() => setBulkTimeOption(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }
    return (
      <div className="cadence-performance-chart--group-by">
        <span>Group Data By</span>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
          <option value="days">Days</option>
          <option value="weeks">Weeks</option>
          <option value="months">Months</option>
        </select>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cadence-performance-chart">
        <Loading />
      </div>
    );
  }

  const renderLegend = () => (
    <div className="cadence-performance-chart--legend">
      {seriesConfig.map((series) => (
        <button
          key={series.label}
          type="button"
          className={`cadence-performance-chart--legend-pill ${hiddenSeries.has(series.label) ? 'is-hidden' : ''}`}
          onClick={() => toggleSeries(series.label)}
          style={
            hiddenSeries.has(series.label)
              ? { borderColor: '#d1d5db', color: '#9ca3af' }
              : { borderColor: series.color, color: series.color }
          }
        >
          <span
            className="cadence-performance-chart--legend-dot"
            style={{ background: hiddenSeries.has(series.label) ? '#d1d5db' : series.color }}
          />
          {series.label}
        </button>
      ))}
    </div>
  );

  if (data.length === 0) {
    return (
      <div className="cadence-performance-chart">
        <div className="cadence-performance-chart--header">
          <h3 className="cadence-performance-chart--title">{title}</h3>
          <div className="cadence-performance-chart--controls">{renderControls()}</div>
        </div>
        <p className="cadence-performance-chart--empty-message">No performance data available for this time range.</p>
        {renderLegend()}
      </div>
    );
  }

  // For daily grouping, pad data to fill the full date range so bars appear
  // at the correct position (e.g., today's data near the right on a 7-day view)
  const chartData = (() => {
    if (!isBulk || data.length === 0) return data;
    const opt = BULK_TIME_OPTIONS.find((o) => o.value === bulkTimeOption);
    if (!opt || opt.grouping !== 'DAY') return data;
    const days = bulkTimeOption === '7d' ? 7 : bulkTimeOption === '30d' ? 30 : 0;
    if (!days) return data;

    // Convert backend dates (YYYY-MM-DD or display labels) to a normalized label map
    const today = new Date();
    const toLabel = (dateStr) => {
      if (!dateStr) return '';
      // Parse YYYY-MM-DD as local date
      const parts = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (parts) {
        const d = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      // Already a display label like "Mar 28" or "Apr 01" — normalize via Date parse
      // to strip leading zeros (backend returns "Apr 01", JS produces "Apr 1")
      const parsed = new Date(`${dateStr}, ${today.getFullYear()}`);
      if (!isNaN(parsed)) {
        return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return dateStr;
    };

    const dataMap = {};
    data.forEach((d) => {
      const label = toLabel(d.date);
      dataMap[label] = { ...d, date: label };
    });

    const padded = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      padded.push(dataMap[label] || { date: label });
    }
    return padded;
  })();

  // Bulk messages always use bar chart; cadence steps use bars when ≤3 data points
  const useBarMode = isBulk || chartData.length <= 3;

  const sharedAxisProps = {
    xAxis: {
      dataKey: 'date',
      tick: { fontSize: 11, fill: '#6b7280' },
      axisLine: { stroke: '#e5e7eb' },
      tickLine: false,
    },
    yAxis: {
      tick: { fontSize: 11, fill: '#6b7280' },
      axisLine: false,
      tickLine: false,
      allowDecimals: false,
    },
    grid: {
      strokeDasharray: '3 3',
      stroke: '#f3f4f6',
      vertical: true,
    },
  };

  return (
    <div className="cadence-performance-chart">
      <div className="cadence-performance-chart--header">
        <h3 className="cadence-performance-chart--title">{title}</h3>
        <div className="cadence-performance-chart--controls">{renderControls()}</div>
      </div>

      <div className="cadence-performance-chart--chart" style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          {useBarMode ? (
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid {...sharedAxisProps.grid} />
              <XAxis {...sharedAxisProps.xAxis} />
              <YAxis {...sharedAxisProps.yAxis} />
              <RechartsTooltip content={<ChartTooltip />} />
              {visibleSeries.map((series) => (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={series.label}
                  fill={series.color}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={36}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
              <CartesianGrid {...sharedAxisProps.grid} />
              <XAxis {...sharedAxisProps.xAxis} />
              <YAxis {...sharedAxisProps.yAxis} />
              <RechartsTooltip content={<ChartTooltip />} />
              {visibleSeries.map((series) => (
                <Line
                  key={series.key}
                  type="monotone"
                  dataKey={series.key}
                  name={series.label}
                  stroke={series.color}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: series.color }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {renderLegend()}
    </div>
  );
};

CadencePerformanceChart.propTypes = {
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  cadenceEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timeframe: PropTypes.string,
  variant: PropTypes.string,
  title: PropTypes.string,
  isManualStep: PropTypes.bool,
};

export default CadencePerformanceChart;
